/**
 * v1 Delete Allowed Domain Dialog Component
 * Modal for confirming domain policy deletion
 */

import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { v1AllowedDomainsApi } from '../api'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Shield, AlertCircle } from 'lucide-react'

interface DeleteAllowedDomainDialogProps {
  open: boolean
  policyId: string | null
  onClose: () => void
  onConfirm: (id: string) => void
}

export function DeleteAllowedDomainDialog({ 
  open, 
  policyId, 
  onClose, 
  onConfirm 
}: DeleteAllowedDomainDialogProps) {
  const queryClient = useQueryClient()

  const deleteMutation = useMutation({
    mutationFn: (id: string) => v1AllowedDomainsApi.deleteAllowedDomain(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['allowedDomains'] })
      toast.success('Policy deleted successfully')
      onClose()
    },
    onError: () => {
      toast.error('Failed to delete policy')
    },
  })

  const handleConfirm = () => {
    if (policyId) {
      deleteMutation.mutate(policyId)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-destructive" />
            Delete Domain Policy
          </DialogTitle>
          <DialogDescription>
            Are you sure you want to delete this domain policy? This action cannot be undone.
          </DialogDescription>
        </DialogHeader>

        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Deleting this policy will remove domain restrictions. Users from any domain will be able to register.
          </AlertDescription>
        </Alert>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? 'Deleting...' : 'Delete Policy'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}